import React, { useState, useEffect } from "react";
import "../styles/MacroCalculator.css";

const MacroCalculator = () => {
  const [searchType, setSearchType] = useState("ingredient");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isMetric, setIsMetric] = useState(true);
  const [savedCombinations, setSavedCombinations] = useState([]);
  const [combinationName, setCombinationName] = useState("");
  const [showSaved, setShowSaved] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [recipeIngredients, setRecipeIngredients] = useState({});
  const [expandedRecipes, setExpandedRecipes] = useState({});
  const [loadingRecipeIngredients, setLoadingRecipeIngredients] = useState({});
  const [sections, setSections] = useState([{ id: 'default', name: 'General', isDefault: true, servings: 1 }]);
  const [currentSection, setCurrentSection] = useState('default');
  const [newSectionName, setNewSectionName] = useState('');
  const [showSectionInput, setShowSectionInput] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [draggedSection, setDraggedSection] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showDetailedMacros, setShowDetailedMacros] = useState(true);

  useEffect(() => {
    fetchUserPreferences();
    fetchSavedCombinations();
    fetchAvailableUnits();
  }, []);

  useEffect(() => {
    // Search as user types with debounce
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length > 0) {
        handleSearch();
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchType]);

  const fetchUserPreferences = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/getUser`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setIsMetric(data.user.default_unit_of_measurement === "metric");
      }
    } catch (error) {
      console.error("Error fetching user preferences:", error);
    }
  };

  const fetchSavedCombinations = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/saved-combinations`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched saved combinations:', data);
        setSavedCombinations(data);
      }
    } catch (error) {
      console.error("Error fetching saved combinations:", error);
    }
  };

  const fetchAvailableUnits = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/units`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setAvailableUnits(data);
      }
    } catch (error) {
      console.error("Error fetching available units:", error);
    }
  };

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      const endpoint = searchType === "ingredient" 
        ? `/api/macro-calculator/search-ingredients`
        : `/api/macro-calculator/search-recipes`;
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${endpoint}?search=${searchQuery}`,
        { credentials: "include" }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
        setShowDropdown(true);
      }
    } catch (error) {
      console.error("Error searching:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const fetchRecipeIngredients = async (recipeId) => {
    setLoadingRecipeIngredients(prev => ({ ...prev, [recipeId]: true }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-ingredients/${recipeId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const ingredients = await response.json();
        setRecipeIngredients(prev => ({ ...prev, [recipeId]: ingredients }));
      }
    } catch (error) {
      console.error("Error fetching recipe ingredients:", error);
    } finally {
      setLoadingRecipeIngredients(prev => ({ ...prev, [recipeId]: false }));
    }
  };

  const toggleRecipeExpanded = async (recipeId) => {
    const isExpanded = expandedRecipes[recipeId];
    setExpandedRecipes(prev => ({ ...prev, [recipeId]: !isExpanded }));
    
    // Fetch ingredients if not already loaded
    if (!isExpanded && !recipeIngredients[recipeId]) {
      await fetchRecipeIngredients(recipeId);
    }
  };


  const addItem = async (item) => {
    if (searchType === "recipe") {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-macros/${item.recipe_id || item.id}`,
          { credentials: "include" }
        );
        if (response.ok) {
          const macroData = await response.json();
          const recipeId = item.recipe_id || item.id;
          
          console.log('Recipe macro data from API:', {
            name: item.name,
            macroData,
            servings: macroData.servings,
            caloriesPerServing: macroData.calories,
            totalCalories: macroData.calories * (macroData.servings || 1)
          });
          
          // Add the recipe with its original serving size
          const recipeServings = macroData.servings || 1;
          
          // API already returns per-serving macros, so use them directly
          setSelectedItems([...selectedItems, {
            ...item,
            ...macroData,
            type: "recipe",
            quantity: recipeServings, // Use the recipe's original servings
            originalServings: recipeServings, // Store original servings for reference
            id: `recipe-${recipeId}-${Date.now()}`,
            section: currentSection
          }]);
          
          // Fetch recipe ingredients
          let ingredients = recipeIngredients[recipeId];
          if (!ingredients) {
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/macro-calculator/recipe-ingredients/${recipeId}`,
              { credentials: "include" }
            );
            if (response.ok) {
              ingredients = await response.json();
              setRecipeIngredients(prev => ({ ...prev, [recipeId]: ingredients }));
            }
          }
          
          // Auto-add all recipe ingredients
          if (ingredients) {
            const newItems = ingredients.map(ing => ({
              ...ing,
              type: "ingredient",
              fromRecipe: recipeId,
              id: `recipe-ingredient-${recipeId}-${ing.ingredient_id}-${Date.now()}`,
              ingredient_name: ing.ingredient_name,
              ingredient_id: ing.ingredient_id,
              custom_ingredient: ing.custom_ingredient,
              quantity: ing.quantity || 1,
              unit: ing.unit || ing.unit_name,
              availableUnits: ing.availableUnits || [],
              recipeServings: recipeServings, // Store recipe servings for scaling
              originalQuantity: ing.quantity || 1, // Store original quantity for scaling
              section: currentSection
            }));
            setSelectedItems(prev => [...prev, ...newItems]);
          }
          
          // Auto-expand the recipe to show ingredients
          if (!expandedRecipes[recipeId]) {
            setExpandedRecipes(prev => ({ ...prev, [recipeId]: true }));
          }
        }
      } catch (error) {
        console.error("Error fetching recipe macros:", error);
      }
    } else {
      // For ingredients, use the default unit or first available unit
      const defaultUnit = item.default_unit_name || 
        (item.availableUnits && item.availableUnits.length > 0 
          ? item.availableUnits[0].unit_name 
          : (isMetric ? "g" : "oz"));
      
      console.log('Adding ingredient:', {
        name: item.ingredient_name,
        defaultUnit: item.default_unit_name,
        protein: item.protein,
        carbs: item.carbs,
        fat: item.fat,
        availableUnits: item.availableUnits
      });
      
      setSelectedItems([...selectedItems, {
        ...item,
        type: "ingredient",
        quantity: 1, // Start with quantity 1 instead of 100
        unit: defaultUnit,
        unit_id: item.default_unit_id,
        availableUnits: item.availableUnits || [],
        id: `ingredient-${item.ingredient_id}-${Date.now()}`,
        section: currentSection
      }]);
    }
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  const removeItem = (itemId) => {
    const itemToRemove = selectedItems.find(item => item.id === itemId);
    
    // If removing a recipe, also remove all its ingredients
    if (itemToRemove?.type === 'recipe') {
      const recipeId = itemToRemove.recipe_id || itemToRemove.id;
      setSelectedItems(selectedItems.filter(item => 
        item.id !== itemId && item.fromRecipe !== recipeId
      ));
    } else {
      setSelectedItems(selectedItems.filter(item => item.id !== itemId));
    }
  };

  const updateRecipeIngredientQuantity = (itemId, quantity) => {
    setSelectedItems(selectedItems.map(item => 
      item.id === itemId ? { ...item, quantity: quantity === '' ? '' : parseFloat(quantity) || 0 } : item
    ));
  };

  const updateRecipeIngredientUnit = (itemId, unitName) => {
    setSelectedItems(selectedItems.map(item => {
      if (item.id === itemId) {
        const selectedUnit = item.availableUnits?.find(u => u.unit_name === unitName);
        return { 
          ...item, 
          unit: unitName,
          unit_id: selectedUnit?.unit_id,
          conversionFactor: selectedUnit?.conversion_factor || 1
        };
      }
      return item;
    }));
  };

  const updateItemQuantity = (itemId, quantity) => {
    const newQuantity = quantity === '' ? '' : parseFloat(quantity) || 0;
    
    setSelectedItems(prevItems => {
      // First update the item itself
      const updatedItems = prevItems.map(item => {
        if (item.id === itemId) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
      
      // Then check if it's a recipe and scale its ingredients
      const updatedItem = updatedItems.find(item => item.id === itemId);
      if (updatedItem?.type === 'recipe' && updatedItem.originalServings) {
        const scaleFactor = newQuantity === '' ? 0 : newQuantity / updatedItem.originalServings;
        
        // Update all ingredients from this recipe
        return updatedItems.map(ing => {
          if (ing.fromRecipe === (updatedItem.recipe_id || updatedItem.id) && ing.originalQuantity) {
            return {
              ...ing,
              quantity: scaleFactor === 0 ? 0 : ing.originalQuantity * scaleFactor
            };
          }
          return ing;
        });
      }
      
      return updatedItems;
    });
  };

  const updateItemUnit = (itemId, unitName) => {
    setSelectedItems(selectedItems.map(item => {
      if (item.id === itemId) {
        // Find the conversion factor for the selected unit
        const selectedUnit = item.availableUnits?.find(u => u.unit_name === unitName);
        
        console.log('Changing unit:', {
          ingredient: item.ingredient_name,
          fromUnit: item.unit,
          toUnit: unitName,
          conversionFactor: selectedUnit?.conversion_factor,
          availableUnits: item.availableUnits
        });
        
        return { 
          ...item, 
          unit: unitName,
          unit_id: selectedUnit?.unit_id,
          conversionFactor: selectedUnit?.conversion_factor || 1
        };
      }
      return item;
    }));
  };

  const handleAddUnit = (item) => {
    // Redirect to the user ingredients page to configure units for this ingredient
    if (item.type === 'custom') {
      window.open(`/user_ingredients?highlight=${item.ingredient_id}`, '_blank');
    } else {
      alert('To add units for this ingredient, please add it to your custom ingredients first.');
    }
  };

  const addSection = () => {
    if (newSectionName.trim()) {
      const newSection = {
        id: `section-${Date.now()}`,
        name: newSectionName.trim(),
        isDefault: false,
        servings: 1
      };
      setSections([...sections, newSection]);
      setCurrentSection(newSection.id);
      setNewSectionName('');
      setShowSectionInput(false);
    }
  };

  const toggleSectionCollapse = (sectionId) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleDragStart = (e, section, index) => {
    setDraggedSection({ section, index });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedSection(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (!draggedSection) return;

    const newSections = [...sections];
    const [movedSection] = newSections.splice(draggedSection.index, 1);
    newSections.splice(dropIndex, 0, movedSection);
    setSections(newSections);
    setDraggedSection(null);
    setDragOverIndex(null);
  };

  const updateSectionServings = (sectionId, servings) => {
    setSections(sections.map(section => 
      section.id === sectionId ? { ...section, servings: servings === '' ? '' : parseInt(servings) || 1 } : section
    ));
  };

  const calculateSectionMacros = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    const sectionItems = selectedItems.filter(item => item.section === sectionId);
    
    const totals = sectionItems.reduce((acc, item) => {
      // Skip recipe ingredients that belong to a recipe (they're counted with the recipe)
      if (item.fromRecipe) return acc;
      
      let itemTotal = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 };
      
      if (item.type === "ingredient") {
        const qty = item.quantity === '' ? 0 : item.quantity;
        let quantityInDefaultUnit;
        if (item.unit === item.default_unit_name || !item.conversionFactor || item.conversionFactor === 1) {
          quantityInDefaultUnit = qty;
        } else {
          quantityInDefaultUnit = qty * item.conversionFactor;
        }
        
        itemTotal = {
          calories: (parseFloat(item.calories) || 0) * quantityInDefaultUnit,
          protein: (parseFloat(item.protein) || 0) * quantityInDefaultUnit,
          carbs: (parseFloat(item.carbs) || 0) * quantityInDefaultUnit,
          fat: (parseFloat(item.fat) || 0) * quantityInDefaultUnit,
          fiber: (parseFloat(item.fiber) || 0) * quantityInDefaultUnit,
          sugar: (parseFloat(item.sugar) || 0) * quantityInDefaultUnit,
        };
      } else if (item.type === "recipe") {
        // For recipes, sum up their ingredients
        const recipeIngredients = selectedItems.filter(si => 
          si.fromRecipe === (item.recipe_id || item.id) && si.type === "ingredient"
        );
        
        itemTotal = recipeIngredients.reduce((recAcc, ing) => {
          const qty = ing.quantity === '' ? 0 : ing.quantity;
          const multiplier = ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
            ? qty
            : qty * ing.conversionFactor;
          
          return {
            calories: recAcc.calories + (parseFloat(ing.calories) || 0) * multiplier,
            protein: recAcc.protein + (parseFloat(ing.protein) || 0) * multiplier,
            carbs: recAcc.carbs + (parseFloat(ing.carbs) || 0) * multiplier,
            fat: recAcc.fat + (parseFloat(ing.fat) || 0) * multiplier,
            fiber: recAcc.fiber + (parseFloat(ing.fiber) || 0) * multiplier,
            sugar: recAcc.sugar + (parseFloat(ing.sugar) || 0) * multiplier,
          };
        }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });
      }
      
      return {
        calories: acc.calories + itemTotal.calories,
        protein: acc.protein + itemTotal.protein,
        carbs: acc.carbs + itemTotal.carbs,
        fat: acc.fat + itemTotal.fat,
        fiber: acc.fiber + itemTotal.fiber,
        sugar: acc.sugar + itemTotal.sugar,
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });
    
    // Divide by section servings
    const sectionServings = (section?.servings === '' || !section?.servings) ? 1 : section.servings;
    return {
      calories: totals.calories / sectionServings,
      protein: totals.protein / sectionServings,
      carbs: totals.carbs / sectionServings,
      fat: totals.fat / sectionServings,
      fiber: totals.fiber / sectionServings,
      sugar: totals.sugar / sectionServings
    };
  };

  const removeSection = (sectionId) => {
    if (sections.find(s => s.id === sectionId)?.isDefault) return;
    
    // Move all items from this section to default section
    setSelectedItems(selectedItems.map(item => 
      item.section === sectionId ? { ...item, section: 'default' } : item
    ));
    
    // Remove the section
    setSections(sections.filter(s => s.id !== sectionId));
    
    // If current section was removed, switch to default
    if (currentSection === sectionId) {
      setCurrentSection('default');
    }
  };

  const calculateTotalMacros = () => {
    // Calculate grand total by summing all items (excluding recipe ingredients which are counted with recipes)
    const allItems = selectedItems.filter(item => !item.fromRecipe);
    
    return allItems.reduce((acc, item) => {
      let itemTotal = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 };
      
      if (item.type === "ingredient") {
        const qty = item.quantity === '' ? 0 : item.quantity;
        let quantityInDefaultUnit;
        if (item.unit === item.default_unit_name || !item.conversionFactor || item.conversionFactor === 1) {
          quantityInDefaultUnit = qty;
        } else {
          quantityInDefaultUnit = qty * item.conversionFactor;
        }
        
        itemTotal = {
          calories: (parseFloat(item.calories) || 0) * quantityInDefaultUnit,
          protein: (parseFloat(item.protein) || 0) * quantityInDefaultUnit,
          carbs: (parseFloat(item.carbs) || 0) * quantityInDefaultUnit,
          fat: (parseFloat(item.fat) || 0) * quantityInDefaultUnit,
          fiber: (parseFloat(item.fiber) || 0) * quantityInDefaultUnit,
          sugar: (parseFloat(item.sugar) || 0) * quantityInDefaultUnit,
        };
      } else if (item.type === "recipe") {
        // For recipes, sum up their ingredients
        const recipeIngredients = selectedItems.filter(si => 
          si.fromRecipe === (item.recipe_id || item.id) && si.type === "ingredient"
        );
        
        itemTotal = recipeIngredients.reduce((recAcc, ing) => {
          const qty = ing.quantity === '' ? 0 : ing.quantity;
          const multiplier = ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
            ? qty
            : qty * ing.conversionFactor;
          
          return {
            calories: recAcc.calories + (parseFloat(ing.calories) || 0) * multiplier,
            protein: recAcc.protein + (parseFloat(ing.protein) || 0) * multiplier,
            carbs: recAcc.carbs + (parseFloat(ing.carbs) || 0) * multiplier,
            fat: recAcc.fat + (parseFloat(ing.fat) || 0) * multiplier,
            fiber: recAcc.fiber + (parseFloat(ing.fiber) || 0) * multiplier,
            sugar: recAcc.sugar + (parseFloat(ing.sugar) || 0) * multiplier,
          };
        }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });
      }
      
      return {
        calories: acc.calories + itemTotal.calories,
        protein: acc.protein + itemTotal.protein,
        carbs: acc.carbs + itemTotal.carbs,
        fat: acc.fat + itemTotal.fat,
        fiber: acc.fiber + itemTotal.fiber,
        sugar: acc.sugar + itemTotal.sugar,
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });
  };

  const saveCombination = async () => {
    if (!combinationName.trim() || selectedItems.length === 0) {
      alert("Please enter a name and add at least one item");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/save-combination`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: combinationName,
            items: selectedItems.map(item => ({
              ...item,
              // Ensure we save all necessary fields
              id: item.id,
              type: item.type,
              section: item.section,
              quantity: item.quantity,
              unit: item.unit,
              originalServings: item.originalServings,
              fromRecipe: item.fromRecipe,
              originalQuantity: item.originalQuantity
            })),
            sections: sections // Save all sections with their servings
          }),
        }
      );

      if (response.ok) {
        alert("Combination saved successfully!");
        setCombinationName("");
        fetchSavedCombinations();
      }
    } catch (error) {
      console.error("Error saving combination:", error);
      alert("Error saving combination");
    }
  };

  const loadCombination = (combination) => {
    console.log('Loading combination:', combination);
    
    // Clear current items first
    setSelectedItems([]);
    
    // Load sections with their servings
    if (combination.sections && combination.sections.length > 0) {
      // Ensure sections have required fields
      const validSections = combination.sections.map(section => ({
        id: section.id || `section-${Date.now()}`,
        name: section.name || 'Unnamed Section',
        isDefault: section.isDefault || false,
        servings: section.servings || 1
      }));
      setSections(validSections);
    } else {
      // For older saved combinations, add servings to existing sections
      setSections([
        { id: 'default', name: 'General', isDefault: true, servings: combination.servings || 1 }
      ]);
    }
    
    // Load items - ensure they have all required fields
    if (combination.items && combination.items.length > 0) {
      const validItems = combination.items.map(item => ({
        ...item,
        id: item.id || `loaded-${Date.now()}-${Math.random()}`,
        section: item.section || 'default',
        quantity: item.quantity === undefined ? 1 : item.quantity
      }));
      
      // Set items after a small delay to ensure sections are set first
      setTimeout(() => {
        setSelectedItems(validItems);
      }, 100);
    }
    
    setShowSaved(false);
  };

  const deleteCombination = async (id) => {
    if (!window.confirm("Are you sure you want to delete this combination?")) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/macro-calculator/combination/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        fetchSavedCombinations();
      }
    } catch (error) {
      console.error("Error deleting combination:", error);
    }
  };

  const totals = calculateTotalMacros();

  return (
    <div className="macro-calculator-container">
      <h1>Macro Calculator</h1>
      
      <div className="macros-display-section full-width">
        <h2>Total Macros</h2>
        
        <div className="macros-grid">
          <div className="macro-item calories">
            <span className="macro-label">Calories</span>
            <span className="macro-value">{totals.calories.toFixed(0)}</span>
          </div>
          <div className="macro-item protein">
            <span className="macro-label">Protein</span>
            <span className="macro-value">{totals.protein.toFixed(1)}g</span>
          </div>
          <div className="macro-item carbs">
            <span className="macro-label">Carbs</span>
            <span className="macro-value">{totals.carbs.toFixed(1)}g</span>
          </div>
          <div className="macro-item fat">
            <span className="macro-label">Fat</span>
            <span className="macro-value">{totals.fat.toFixed(1)}g</span>
          </div>
          <div className="macro-item fiber">
            <span className="macro-label">Fiber</span>
            <span className="macro-value">{totals.fiber.toFixed(1)}g</span>
          </div>
          <div className="macro-item sugar">
            <span className="macro-label">Sugar</span>
            <span className="macro-value">{totals.sugar.toFixed(1)}g</span>
          </div>
        </div>

        {selectedItems.length > 0 && (
          <div className="save-combination">
            <input
              type="text"
              placeholder="Combination name..."
              value={combinationName}
              onChange={(e) => setCombinationName(e.target.value)}
            />
            <button onClick={saveCombination} className="save-button">
              💾 Save Combination
            </button>
          </div>
        )}
      </div>
      
      <div className="add-items-row">
        <div className="search-section">
          <h2>Add Items</h2>
          
          <div className="search-type-toggle">
            <button
              className={searchType === "ingredient" ? "active" : ""}
              onClick={() => setSearchType("ingredient")}
            >
              Ingredients
            </button>
            <button
              className={searchType === "recipe" ? "active" : ""}
              onClick={() => setSearchType("recipe")}
            >
              Recipes
            </button>
          </div>

          <div className="search-container">
            <div className="search-bar">
              <input
                type="text"
                placeholder={`Search ${searchType}s...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery && setShowDropdown(true)}
                onBlur={(e) => {
                  // Delay hiding dropdown to allow click on results
                  if (!e.relatedTarget?.closest('.search-results')) {
                    setTimeout(() => setShowDropdown(false), 200);
                  }
                }}
              />
              {isSearching && <span className="loading-spinner">⟳</span>}
            </div>

            {showDropdown && searchResults.length > 0 && (
              <div className="search-results">
                {searchResults.map((result) => (
                  <div 
                    key={result.ingredient_id || result.recipe_id || result.id} 
                    className="search-result-item"
                    onClick={() => addItem(result)}
                    tabIndex="0"
                  >
                    <div className="result-info">
                      <span className="result-name">{result.ingredient_name || result.name}</span>
                      {searchType === "ingredient" && (
                        <span className="result-macros">
                          {result.calories?.toFixed(0) || 0} cal • 
                          {result.protein || 0}g protein • 
                          {result.carbs || 0}g carbs • 
                          {result.fat || 0}g fat
                        </span>
                      )}
                    </div>
                    <button className="add-button" onClick={(e) => {
                      e.stopPropagation();
                      addItem(result);
                    }}>
                      +
                    </button>
                  </div>
                ))}
              </div>
            )}

            {showDropdown && searchQuery && searchResults.length === 0 && !isSearching && (
              <div className="search-results">
                <div className="no-results">No {searchType}s found</div>
              </div>
            )}
          </div>
        </div>
        
        <div className="section-management">
          <h2>Sections</h2>
          
          <div className="section-controls">
            <select 
              value={currentSection} 
              onChange={(e) => setCurrentSection(e.target.value)}
              className="section-select"
            >
              {sections.map(section => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
            </select>
            
            {!showSectionInput ? (
              <button 
                className="add-section-btn"
                onClick={() => setShowSectionInput(true)}
                title="Add new section"
              >
                + Section
              </button>
            ) : (
              <div className="new-section-input">
                <input
                  type="text"
                  placeholder="Section name..."
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addSection()}
                  autoFocus
                />
                <button onClick={addSection} className="confirm-btn">✓</button>
                <button onClick={() => {
                  setShowSectionInput(false);
                  setNewSectionName('');
                }} className="cancel-btn">✗</button>
              </div>
            )}
            
            {currentSection !== 'default' && (
              <button 
                className="remove-section-btn"
                onClick={() => removeSection(currentSection)}
                title="Remove current section"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="selected-items-section full-width">
          <div className="selected-items-header">
            <h2>Selected Items</h2>
            <button 
              className="toggle-detail-btn"
              onClick={() => setShowDetailedMacros(!showDetailedMacros)}
              title={showDetailedMacros ? "Hide macro details" : "Show macro details"}
            >
              {showDetailedMacros ? '📊 Detailed' : '📊 Compact'}
            </button>
          </div>

          {selectedItems.length === 0 ? (
            <p className="empty-message">No items added yet</p>
          ) : (
            <div className="selected-items-list">
              {sections.map((section, index) => {
                const sectionItems = selectedItems.filter(item => !item.fromRecipe && item.section === section.id);
                if (sectionItems.length === 0) return null;
                
                const sectionMacros = calculateSectionMacros(section.id);
                const isCollapsed = collapsedSections[section.id];
                const isDragging = draggedSection?.section.id === section.id;
                const isDragOver = dragOverIndex === index;
                
                return (
                  <div 
                    key={section.id} 
                    className={`section-group ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, section, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    onDrop={(e) => handleDrop(e, index)}
                  >
                    <div className="section-header-wrapper">
                      <h3 className="section-header">
                        <span className="drag-handle">⋮⋮</span>
                        <button 
                          className="collapse-toggle"
                          onClick={() => toggleSectionCollapse(section.id)}
                        >
                          {isCollapsed ? '▶' : '▼'}
                        </button>
                        {section.name}
                      </h3>
                      <div className="section-controls-right">
                        <div className="section-servings">
                          <label>Servings:</label>
                          <input
                            type="number"
                            min="1"
                            value={section.servings === '' ? '' : (section.servings || 1)}
                            onChange={(e) => updateSectionServings(section.id, e.target.value)}
                            className="servings-input-small"
                          />
                        </div>
                        <div className="section-macros">
                          <span className="section-macro">{sectionMacros.calories.toFixed(0)} cal</span>
                          <span className="section-macro">{sectionMacros.protein.toFixed(1)}g protein</span>
                          <span className="section-macro">{sectionMacros.carbs.toFixed(1)}g carbs</span>
                          <span className="section-macro">{sectionMacros.fat.toFixed(1)}g fat</span>
                          <span className="section-macro-label">per serving</span>
                        </div>
                      </div>
                    </div>
                    {!isCollapsed && (
                      <div className="section-items">
                        {sectionItems.map((item) => (
                <React.Fragment key={item.id}>
                <div className={`selected-item ${item.type === 'recipe' ? 'recipe-item' : ''}`}>
                  <div className="item-info">
                    <span className="item-name">
                      {item.type === "recipe" && (
                        <button 
                          className="expand-toggle"
                          onClick={() => toggleRecipeExpanded(item.recipe_id || item.id)}
                          title={expandedRecipes[item.recipe_id || item.id] ? "Collapse" : "Expand"}
                        >
                          {expandedRecipes[item.recipe_id || item.id] ? '▼' : '▶'}
                        </button>
                      )}
                      {item.ingredient_name || item.name}
                      <span className="item-type"> ({item.type})</span>
                      {item.customConversion && (
                        <span className="conversion-warning" title="This unit conversion is approximate">
                          ⚠️
                        </span>
                      )}
                    </span>
                    <div className="quantity-input">
                      {item.type === "ingredient" ? (
                        <>
                          <input
                            type="number"
                            value={item.quantity === '' ? '' : item.quantity}
                            onChange={(e) => updateItemQuantity(item.id, e.target.value)}
                          />
                          {item.availableUnits && item.availableUnits.length > 0 ? (
                          <select 
                            value={item.unit}
                            onChange={(e) => updateItemUnit(item.id, e.target.value)}
                            className="unit-select"
                          >
                            {item.availableUnits.map(unit => (
                              <option key={unit.unit_id} value={unit.unit_name}>
                                {unit.unit_name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="no-units-text">
                            {item.unit}
                            <button 
                              className="add-unit-btn"
                              onClick={() => handleAddUnit(item)}
                              title="Configure units for this ingredient"
                            >
                              +
                            </button>
                          </span>
                        )}
                        </>
                      ) : (
                        <span className="recipe-servings-display">Recipe: {item.originalServings || item.servings || 1} servings</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => removeItem(item.id)} className="remove-button">
                    ×
                  </button>
                </div>
                {item.type === "ingredient" && showDetailedMacros && (
                  <div className="item-macros-row">
                    <span className="macro-item">
                      <span className="macro-value">{((item.calories || 0) * (item.quantity === '' ? 0 : item.quantity) * (item.conversionFactor || 1)).toFixed(0)}</span>
                      <span className="macro-label">cal</span>
                    </span>
                    <span className="macro-divider">|</span>
                    <span className="macro-item">
                      <span className="macro-value">{((item.protein || 0) * (item.quantity === '' ? 0 : item.quantity) * (item.conversionFactor || 1)).toFixed(1)}g</span>
                      <span className="macro-label">protein</span>
                    </span>
                    <span className="macro-divider">|</span>
                    <span className="macro-item">
                      <span className="macro-value">{((item.carbs || 0) * (item.quantity === '' ? 0 : item.quantity) * (item.conversionFactor || 1)).toFixed(1)}g</span>
                      <span className="macro-label">carbs</span>
                    </span>
                    <span className="macro-divider">|</span>
                    <span className="macro-item">
                      <span className="macro-value">{((item.fat || 0) * (item.quantity === '' ? 0 : item.quantity) * (item.conversionFactor || 1)).toFixed(1)}g</span>
                      <span className="macro-label">fat</span>
                    </span>
                    <span className="macro-divider">|</span>
                    <span className="macro-item">
                      <span className="macro-value">{((item.fiber || 0) * (item.quantity === '' ? 0 : item.quantity) * (item.conversionFactor || 1)).toFixed(1)}g</span>
                      <span className="macro-label">fiber</span>
                    </span>
                    <span className="macro-divider">|</span>
                    <span className="macro-item">
                      <span className="macro-value">{((item.sugar || 0) * (item.quantity === '' ? 0 : item.quantity) * (item.conversionFactor || 1)).toFixed(1)}g</span>
                      <span className="macro-label">sugar</span>
                    </span>
                  </div>
                )}
                {item.type === "recipe" && showDetailedMacros && (() => {
                  // Calculate recipe macros from its ingredients
                  const recipeIngredients = selectedItems.filter(si => 
                    si.fromRecipe === (item.recipe_id || item.id) && si.type === "ingredient"
                  );
                  
                  const recipeTotals = recipeIngredients.reduce((acc, ing) => {
                    const multiplier = ing.unit === ing.default_unit_name || !ing.conversionFactor || ing.conversionFactor === 1
                      ? ing.quantity
                      : ing.quantity * ing.conversionFactor;
                    
                    return {
                      calories: acc.calories + (parseFloat(ing.calories) || 0) * multiplier,
                      protein: acc.protein + (parseFloat(ing.protein) || 0) * multiplier,
                      carbs: acc.carbs + (parseFloat(ing.carbs) || 0) * multiplier,
                      fat: acc.fat + (parseFloat(ing.fat) || 0) * multiplier,
                      fiber: acc.fiber + (parseFloat(ing.fiber) || 0) * multiplier,
                      sugar: acc.sugar + (parseFloat(ing.sugar) || 0) * multiplier,
                    };
                  }, { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0 });
                  
                  return (
                    <div className="item-macros-row">
                      <span className="macro-item">
                        <span className="macro-value">{recipeTotals.calories.toFixed(0)}</span>
                        <span className="macro-label">cal</span>
                      </span>
                      <span className="macro-divider">|</span>
                      <span className="macro-item">
                        <span className="macro-value">{recipeTotals.protein.toFixed(1)}g</span>
                        <span className="macro-label">protein</span>
                      </span>
                      <span className="macro-divider">|</span>
                      <span className="macro-item">
                        <span className="macro-value">{recipeTotals.carbs.toFixed(1)}g</span>
                        <span className="macro-label">carbs</span>
                      </span>
                      <span className="macro-divider">|</span>
                      <span className="macro-item">
                        <span className="macro-value">{recipeTotals.fat.toFixed(1)}g</span>
                        <span className="macro-label">fat</span>
                      </span>
                      <span className="macro-divider">|</span>
                      <span className="macro-item">
                        <span className="macro-value">{recipeTotals.fiber.toFixed(1)}g</span>
                        <span className="macro-label">fiber</span>
                      </span>
                      <span className="macro-divider">|</span>
                      <span className="macro-item">
                        <span className="macro-value">{recipeTotals.sugar.toFixed(1)}g</span>
                        <span className="macro-label">sugar</span>
                      </span>
                    </div>
                  );
                })()}
                {item.type === "recipe" && expandedRecipes[item.recipe_id || item.id] && (
                  <div className="recipe-ingredients-dropdown">
                    {loadingRecipeIngredients[item.recipe_id || item.id] ? (
                      <div className="loading-ingredients">Loading ingredients...</div>
                    ) : (
                      <>
                        <div className="recipe-ingredients-header">
                          <span>Recipe Ingredients:</span>
                        </div>
                        <div className="recipe-ingredients-list">
                          {selectedItems
                            .filter(si => si.fromRecipe === (item.recipe_id || item.id) && si.type === "ingredient")
                            .map(ingredient => {
                              return (
                                <div key={ingredient.id} className="recipe-ingredient-item-wrapper">
                                  <div className="recipe-ingredient-item with-controls">
                                    <div className="ingredient-info">
                                      <span className="ingredient-name">{ingredient.ingredient_name}</span>
                                    </div>
                                    <div className="ingredient-controls">
                                    <input
                                      type="number"
                                      className="ingredient-quantity"
                                      value={ingredient.quantity === '' ? '' : ingredient.quantity}
                                      onChange={(e) => updateRecipeIngredientQuantity(ingredient.id, e.target.value)}
                                    />
                                    {ingredient.availableUnits && ingredient.availableUnits.length > 0 ? (
                                      <select 
                                        value={ingredient.unit}
                                        onChange={(e) => updateRecipeIngredientUnit(ingredient.id, e.target.value)}
                                        className="ingredient-unit-select"
                                      >
                                        {ingredient.availableUnits.map(unit => (
                                          <option key={unit.unit_id} value={unit.unit_name}>
                                            {unit.unit_name}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <span className="ingredient-unit-text">{ingredient.unit}</span>
                                    )}
                                    <button 
                                      onClick={() => removeItem(ingredient.id)} 
                                      className="ingredient-remove-btn"
                                      title="Remove this ingredient"
                                    >
                                      ×
                                    </button>
                                  </div>
                                </div>
                                {showDetailedMacros && (
                                <div className="ingredient-macros-row">
                                  <span className="macro-item">
                                    <span className="macro-value">{((ingredient.calories || 0) * (ingredient.quantity === '' ? 0 : ingredient.quantity) * (ingredient.conversionFactor || 1)).toFixed(0)}</span>
                                    <span className="macro-label">cal</span>
                                  </span>
                                  <span className="macro-divider">|</span>
                                  <span className="macro-item">
                                    <span className="macro-value">{((ingredient.protein || 0) * (ingredient.quantity === '' ? 0 : ingredient.quantity) * (ingredient.conversionFactor || 1)).toFixed(1)}g</span>
                                    <span className="macro-label">protein</span>
                                  </span>
                                  <span className="macro-divider">|</span>
                                  <span className="macro-item">
                                    <span className="macro-value">{((ingredient.carbs || 0) * (ingredient.quantity === '' ? 0 : ingredient.quantity) * (ingredient.conversionFactor || 1)).toFixed(1)}g</span>
                                    <span className="macro-label">carbs</span>
                                  </span>
                                  <span className="macro-divider">|</span>
                                  <span className="macro-item">
                                    <span className="macro-value">{((ingredient.fat || 0) * (ingredient.quantity === '' ? 0 : ingredient.quantity) * (ingredient.conversionFactor || 1)).toFixed(1)}g</span>
                                    <span className="macro-label">fat</span>
                                  </span>
                                  <span className="macro-divider">|</span>
                                  <span className="macro-item">
                                    <span className="macro-value">{((ingredient.fiber || 0) * (ingredient.quantity === '' ? 0 : ingredient.quantity) * (ingredient.conversionFactor || 1)).toFixed(1)}g</span>
                                    <span className="macro-label">fiber</span>
                                  </span>
                                  <span className="macro-divider">|</span>
                                  <span className="macro-item">
                                    <span className="macro-value">{((ingredient.sugar || 0) * (ingredient.quantity === '' ? 0 : ingredient.quantity) * (ingredient.conversionFactor || 1)).toFixed(1)}g</span>
                                    <span className="macro-label">sugar</span>
                                  </span>
                                </div>
                                )}
                              </div>
                              );
                            })}
                        </div>
                      </>
                    )}
                  </div>
                )}
                </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
      </div>

      <div className="saved-combinations-section">
        <button 
          className="toggle-saved"
          onClick={() => setShowSaved(!showSaved)}
        >
          <span>Saved Combinations</span>
          <span>{showSaved ? '▲' : '▼'}</span>
        </button>

        {showSaved && (
          <div className="saved-combinations-list">
            {savedCombinations.length === 0 ? (
              <p className="empty-message">No saved combinations yet</p>
            ) : (
              savedCombinations.map((combination) => (
                <div key={combination.id} className="saved-combination">
                  <div className="combination-info">
                    <h3>{combination.name}</h3>
                    <p>{combination.items?.length || 0} items</p>
                  </div>
                  <div className="combination-actions">
                    <button onClick={() => loadCombination(combination)} className="load-button">
                      Load
                    </button>
                    <button onClick={() => deleteCombination(combination.id)} className="delete-button">
                      ×
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MacroCalculator;